import type {CSSProperties} from 'react';
import {font, leading, radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';
import {wedgeFormFieldStyle} from './wedgeFormField.js';

export type WedgeTextareaVariant = 'wedge-answer-area' | 'wedge-refinement-area' | 'wedge-settings-area' | 'wedge-note-area' | 'wedge-notecard-area' | 'wedge-outcome-area' | 'wedge-emailc-body-area' | 'wedge-brief-feedback-area';

export const WEDGE_TEXTAREA_VARIANTS: readonly WedgeTextareaVariant[] = [
  'wedge-answer-area', 'wedge-refinement-area', 'wedge-settings-area', 'wedge-note-area', 'wedge-notecard-area',
  'wedge-outcome-area', 'wedge-emailc-body-area', 'wedge-brief-feedback-area',
];

export const isWedgeTextareaVariant = (variant: unknown): variant is WedgeTextareaVariant =>
  WEDGE_TEXTAREA_VARIANTS.includes(variant as WedgeTextareaVariant);

/* Released multi-line text fields of the prototype, one look per cluster of live sites whose declared values match. Each is a
   bare native textarea. The values are the shipped inline styles, off-scale numbers included, and each color or family reads
   the exact legacy role, so the shipped ink rules and the host theme variables keep matching. A look carries width only when
   every live site declares it. Flex, minimum width, the disabled dim and per-site margins stay with the caller, and so does
   `rows`: every shipped site sets its own. Focus and placeholder come from the shell sheet (`:focus-visible` always wins over
   the inline outline), and hover and ring classes (`ob-input`) from the caller, as before. */
const NOTE_BORDER = 0.5, NOTE_LEADING = 1.4;
export function wedgeTextareaFieldStyle(variant: WedgeTextareaVariant): CSSProperties {
  const r = wedgeExactRole;
  const card = `1px solid ${r('cardBorder')}`;
  switch (variant) {
    /* Onboarding answer editors. The values of the one-line answer field, shared, plus the shipped no-resize handle. */
    case 'wedge-answer-area': return {
      ...wedgeFormFieldStyle('wedge-answer-field'), resize: 'none',
    };
    /* Industry refinement notes: strength and win pattern, saved on blur. The user may drag the height. */
    case 'wedge-refinement-area': return {
      width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: radius.md, background: r('bgElev'), border: card,
      fontSize: font.headline, color: r('label'), fontFamily: r('font'), outline: 0, resize: 'vertical', lineHeight: leading.body,
    };
    /* Selling settings text: what you sell and about you, inside a padded card. The height is the row count. */
    case 'wedge-settings-area': return {
      width: '100%', padding: 12, background: r('bgElev'), border: card, borderRadius: 11, fontSize: font.headline,
      color: r('label'), fontFamily: r('font'), lineHeight: leading.body, resize: 'none', outline: 0, boxSizing: 'border-box',
    };
    /* The inline note editor of a Saved row (Saved tab): a hairline accent border in place of the card border, a 1.4 line height and the
       outline set to none, as shipped. The user may drag the height. Focus is on mount and the caller owns it. */
    case 'wedge-note-area': return {
      width: '100%', boxSizing: 'border-box', resize: 'vertical', padding: '10px 12px', borderRadius: radius.md, background: r('bgElev'),
      color: r('label'), border: `${NOTE_BORDER}px solid ${r('accent')}`, outline: 'none', fontFamily: r('font'), fontSize: font.headline,
      lineHeight: NOTE_LEADING,
    };
    /* The inline editor of a note card (Notes tab): the Saved row note editor on the canvas fill in place of the elevated solid one. Every
       other value is the same, so it is that look with its fill replaced. The canvas is a gradient in the dark and light themes. */
    case 'wedge-notecard-area': return {
      ...wedgeTextareaFieldStyle('wedge-note-area'), background: r('canvas'),
    };
    /* The note of the card that logs a call (Won, Pushed or Lost): the settings text look on the surface fill, with a 12px radius, 16px type
       and the 1.4 line height, no resize and no outline. The height is the row count (3). */
    case 'wedge-outcome-area': return {
      width: '100%', padding: 12, background: r('surface'), border: card, borderRadius: 12, fontSize: font.headline,
      color: r('label'), fontFamily: r('font'), lineHeight: NOTE_LEADING, resize: 'none', outline: 0, boxSizing: 'border-box',
    };
    /* The draft body of the email composer: borderless on the sheet, above the word count, no resize handle. The caller owns the flex, the
       rows, the value and the pane around it. */
    case 'wedge-emailc-body-area': return {
      border: 0, outline: 0, background: 'transparent', padding: '16px 16px 6px', fontSize: 16, color: r('label'),
      fontFamily: r('font'), lineHeight: 1.55, resize: 'none',
    };
    /* The "what was off" note of the Brief feedback card: the settings text look with 10px by 12px padding and the 1.4 line height. The height
       is the row count (2). */
    case 'wedge-brief-feedback-area': return {
      width: '100%', padding: '10px 12px', background: r('bgElev'), border: card, borderRadius: 11, fontSize: 16, color: r('label'),
      fontFamily: r('font'), lineHeight: NOTE_LEADING, resize: 'none', outline: 0, boxSizing: 'border-box',
    };
  }
}
